import React, { useMemo, useContext, useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import { useNavigate, useParams } from "react-router-dom"
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
} from "@mui/x-data-grid-pro"
import i18n from "i18n"

import DataGridLocaleText from "common/DataGridLocaleText"
import { formatToDDMMYYYY } from "utils/dateUtils"
import { compareDates } from "components/Invoices/utils/invoiceUtils"
import AuthContext from "context/auth/authContext"
import ProjectContext from "context/projects/projectContext"
import { useContactsLight } from "components/Contacts/hooks/useContacts"
import { getCurrency } from "utils/functions"
import { currencyFormatDE } from "lib/transformers"
import { getPurchaseOrderRowId } from "../utils/purchaseOrderUtils"
import ActionsMenu from "./ActionsMenu"

const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <GridToolbarQuickFilter
      quickFilterParser={searchInput =>
        searchInput.split(",").map(value => value.trim())
      }
      quickFilterFormatter={quickFilterValues => quickFilterValues.join(", ")}
    />
  </GridToolbarContainer>
)

const PurchaseOrdersDataGrid = ({ purchaseOrders }) => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { user } = useContext(AuthContext)
  const organizationId = user?.["custom:organizationId"]
  const { projects = [], getProjects } = useContext(ProjectContext)
  const { data: contactsLight = [] } = useContactsLight(
    organizationId,
    !!organizationId,
  )
  const currencySymbol = getCurrency(user?.["custom:currency"]) ?? "€"

  useEffect(() => {
    if (projects.length === 0 && getProjects) {
      getProjects()
    }
  }, [projects.length, getProjects])

  const formatProjectsLabel = useCallback(
    row => {
      if (!row.projectId) return ""
      const p = projects.find(pr => pr.projectId === row.projectId)
      return p && p.name ? String(p.name).trim() : ""
    },
    [projects],
  )

  const providerDisplayName = useCallback(
    sk => {
      if (sk == null || sk === "") return ""
      const contact = contactsLight.find(c => c.SK === sk)
      if (!contact) return ""
      return `${contact.firstname || ""} ${contact.lastname || ""}`.trim()
    },
    [contactsLight],
  )

  const list = useMemo(
    () =>
      Array.isArray(purchaseOrders)
        ? purchaseOrders
        : purchaseOrders
          ? [purchaseOrders]
          : [],
    [purchaseOrders],
  )

  const rows = useMemo(
    () =>
      list.map((row, index) => ({
        ...row,
        __poListIndex: index,
        issueDateFormatted: row.issueDate
          ? formatToDDMMYYYY(row.issueDate)
          : "",
        dueDateFormatted: row.dueDate ? formatToDDMMYYYY(row.dueDate) : "",
      })),
    [list],
  )

  const columns = [
    {
      field: "issueDateFormatted",
      headerName: i18n.t("invoices.issueDate"),
      width: 120,
      sortComparator: (v1, v2, param1, param2) => {
        const row1 = param1.api.getRow(param1.id)
        const row2 = param2.api.getRow(param2.id)
        return compareDates(row1, row2, "issueDate")
      },
    },
    {
      field: "dueDateFormatted",
      headerName: i18n.t("invoices.dueDate"),
      width: 120,
      sortComparator: (v1, v2, param1, param2) => {
        const row1 = param1.api.getRow(param1.id)
        const row2 = param2.api.getRow(param2.id)
        return compareDates(row1, row2, "dueDate")
      },
    },
    {
      field: "purchaseOrderNumber",
      headerName: i18n.t("purchaseOrders.purchaseOrderNumber"),
      width: 160,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "projectId",
      headerName: i18n.t("purchaseOrders.project"),
      flex: 1,
      minWidth: 200,
      sortable: false,
      valueGetter: (value, row) => formatProjectsLabel(row),
      renderCell: params => formatProjectsLabel(params.row),
    },
    {
      field: "contact",
      headerName: i18n.t("costs.columns.provider"),
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => providerDisplayName(row.contact),
      renderCell: params => providerDisplayName(params.row.contact),
    },
    {
      field: "subtotal",
      headerName: i18n.t("invoices.subtotal"),
      width: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: params =>
        currencyFormatDE(Number(params.row.subtotal) || 0, currencySymbol, 2),
    },
    {
      field: "total",
      headerName: i18n.t("invoices.total"),
      width: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: params =>
        currencyFormatDE(Number(params.row.total) || 0, currencySymbol, 2),
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <ActionsMenu row={params.row} projectId={projectId} />
      ),
      pinned: "right",
      headerAlign: "center",
      align: "center",
    },
  ]

  const handleCellClick = params => {
    if (params.field === "actions") return

    const id = getPurchaseOrderRowId(params.row)
    if (id != null && String(id) !== "") {
      navigate(
        projectId
          ? `/purchase-order/${id}/${projectId}`
          : `/purchase-order/${id}`,
      )
    }
  }

  const gridRowId = row =>
    String(getPurchaseOrderRowId(row) ?? `row-${row.__poListIndex ?? 0}`)

  return (
    <div
      style={{
        width: "100%",
        height: "min(700px, calc(100vh - 270px))",
      }}
    >
      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={gridRowId}
        onCellClick={handleCellClick}
        pinnedColumns={{ right: ["actions"] }}
        pageSize={10}
        pagination
        pageSizeOptions={[10, 25, 50]}
        slots={{ toolbar: CustomToolbar }}
        localeText={DataGridLocaleText()}
        sx={{
          border: 0,
          "& .MuiDataGrid-toolbarContainer>Button": {
            color: "rgba(0, 0, 0, 0.87)",
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
      />
    </div>
  )
}

PurchaseOrdersDataGrid.propTypes = {
  purchaseOrders: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
}

export default PurchaseOrdersDataGrid
